import { format } from 'date-fns';
import React from 'react';

import { ResourceDatabaseFieldTypeId } from '@/api/enums';
import { AM_PM_TIME_SPACE_FORMAT, MONTH_DAY_YEAR_FORMAT, MONTH_DAY_YEAR_HOUR_FORMAT } from '@/config/variables';
import { useTimezone } from '@/hooks';
import { getDateFromTime, getFileNameFromFileUrl } from '@/helpers';
import { Attachment } from './Attachment';

interface IResourceDatabaseItemValue {
  children: string | number | null;
  columnType: ResourceDatabaseFieldTypeId;
  isPreview?: boolean;
}

const ResourceDatabaseItemValue = ({
  children,
  columnType,
  isPreview,
}: IResourceDatabaseItemValue): React.ReactElement => {
  const { formatDateTime } = useTimezone();

  switch (columnType) {
    case ResourceDatabaseFieldTypeId.WebLink:
      return (
        <a href={children as string} style={{ wordBreak: 'break-all' }} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    case ResourceDatabaseFieldTypeId.FileAttachment: {
      if (!children) return <></>;
      const url = children as string;
      const displayName = getFileNameFromFileUrl(url);
      return isPreview ? (
        <Attachment key={url} fileName={displayName} actions={{ preview: url }} />
      ) : (
        <a href={url} style={{ wordBreak: 'break-all' }} target="_blank" rel="noreferrer">
          {displayName}
        </a>
      );
    }
    case ResourceDatabaseFieldTypeId.Email:
      return <a href={`mailto:${children as string}`}>{children}</a>;
    case ResourceDatabaseFieldTypeId.Checkbox:
      return <input type="checkbox" checked={!!Number(children)} readOnly />;
    case ResourceDatabaseFieldTypeId.Time: {
      const time = children as string;
      const date = getDateFromTime(time);
      return <>{format(date, AM_PM_TIME_SPACE_FORMAT)}</>;
    }
    case ResourceDatabaseFieldTypeId.Date: {
      const date = new Date(`${children}T00:00`);
      return <>{format(date, MONTH_DAY_YEAR_FORMAT)}</>;
    }
    case ResourceDatabaseFieldTypeId.DateTime: {
      return <>{formatDateTime(children as string, MONTH_DAY_YEAR_HOUR_FORMAT)}</>;
    }
    case ResourceDatabaseFieldTypeId.MemberList: {
      if (!children) return <></>;
      const [, name] = (children as string).split('|');
      return <>{name}</>;
    }
    case ResourceDatabaseFieldTypeId.Currency: {
      if (!children) return <></>;
      return <>{`$${children}`}</>;
    }
    default:
      return <>{children}</>;
  }
};

export { ResourceDatabaseItemValue };
